import { Link } from 'react-router-dom'
import {ArrowRight, Clock, FileText, Target, Sparkles} from 'lucide-react'
import Nav from '@/components/Nav'
import Footer from '@/components/Footer'

function Home() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF7]">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60" aria-hidden="true" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#0F766E]/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-[#F59E0B]/10 rounded-full blur-3xl" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0A0A0A]/10 bg-white/60 backdrop-blur-sm mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0F766E] animate-pulse" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#0A0A0A]/70">
                  Free Assessment · No signup
                </span>
              </div>

              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mb-8">
                Is your company
                <br />
                ready to adopt
                <br />
                <span className="relative inline-block">
                  <span className="italic text-[#0F766E]">AI</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#F59E0B]/30 -z-10" aria-hidden="true" />
                </span>
                ?
              </h1>

              <p className="text-lg md:text-xl text-[#0A0A0A]/70 max-w-2xl leading-relaxed mb-10">
                Assess your data maturity, business processes, and organizational readiness in 5 minutes.
                Get a <strong className="text-[#0A0A0A]">tailored AI adoption roadmap</strong> and concrete recommendations, instantly.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Link
                  to="/quiz"
                  className="group inline-flex items-center gap-3 bg-[#0A0A0A] text-[#FAFAF7] px-8 py-5 rounded-full font-medium text-base hover:bg-[#0F766E] transition-all duration-300 hover:gap-5 shadow-lg shadow-[#0A0A0A]/10"
                >
                  <Sparkles size={18} className="text-[#F59E0B]" />
                  Start the assessment
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <div className="flex items-center gap-2 text-sm text-[#0A0A0A]/60">
                  <Clock size={14} />
                  <span>About 5 minutes · 8 questions</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 shadow-xl shadow-[#0A0A0A]/5">
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#0A0A0A]/40 mb-4">
                  Sample Output
                </div>
                <div className="font-display text-4xl font-semibold mb-1">72<span className="text-2xl text-[#0A0A0A]/40">/100</span></div>
                <div className="text-sm text-[#0F766E] font-medium mb-6">Established</div>

                <div className="space-y-3 mb-6">
                  {[
                    { label: 'Data Maturity', val: 80 },
                    { label: 'Business Processes', val: 65 },
                    { label: 'Organization', val: 70 },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-[#0A0A0A]/60">{item.label}</span>
                        <span className="font-mono tabular-nums">{item.val}%</span>
                      </div>
                      <div className="h-1 bg-[#F3F3EE] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0F766E]" style={{ width: `${item.val}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-[#E5E5E5]">
                  <p className="text-xs text-[#0A0A0A]/50 leading-relaxed">
                    "Focus on scaling successful patterns and operating MLOps platforms in full production."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative bg-white border-y border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-2xl mb-16">
            <div className="font-mono text-xs uppercase tracking-widest text-[#0F766E] mb-4">
              How it works
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-medium leading-tight tracking-tight">
              An <span className="italic">objective</span> readiness<br />
              assessment, grounded in data.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                num: '01',
                icon: FileText,
                title: '8 Strategic Areas',
                desc: 'From data maturity to executive commitment, we cover the eight dimensions essential to AI adoption.',
              },
              {
                num: '02',
                icon: Target,
                title: '4-Stage Maturity Model',
                desc: 'From Emerging to Leading, we pinpoint where you are today and define the next move clearly.',
              },
              {
                num: '03',
                icon: Sparkles,
                title: 'Actionable Recommendations',
                desc: 'No abstract theory — get concrete short-term initiatives and a longer-term roadmap.',
              },
            ].map((feature, i) => (
              <div
                key={feature.num}
                className="group fade-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="font-mono text-xs text-[#0A0A0A]/30 mb-6">{feature.num}</div>
                <div className="w-12 h-12 rounded-xl bg-[#F3F3EE] flex items-center justify-center mb-5 group-hover:bg-[#0F766E] group-hover:text-white transition-colors">
                  <feature.icon size={20} />
                </div>
                <h3 className="font-display text-2xl font-semibold mb-3 tracking-tight">{feature.title}</h3>
                <p className="text-[#0A0A0A]/60 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories preview */}
      <section className="bg-[#FAFAF7]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="font-mono text-xs uppercase tracking-widest text-[#0F766E] mb-4">
                Assessment Areas
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-medium leading-tight tracking-tight mb-6">
                The 8 areas<br />we evaluate
              </h2>
              <p className="text-[#0A0A0A]/60 leading-relaxed">
                We quantify maturity in each area so you know exactly which challenges to tackle first.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
                {[
                  'Data Maturity',
                  'Data Quality',
                  'Business Processes',
                  'People & Organization',
                  'Executive Commitment',
                  'Infrastructure',
                  'Use Cases',
                  'Governance',
                ].map((cat, i) => (
                  <div
                    key={cat}
                    className="bg-[#FAFAF7] p-6 hover:bg-white transition-colors group"
                  >
                    <div className="font-mono text-xs text-[#0A0A0A]/40 mb-2">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="font-display text-xl font-semibold group-hover:text-[#0F766E] transition-colors">
                      {cat}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0A0A0A] text-[#FAFAF7] noise">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-medium leading-tight tracking-tight mb-8 max-w-3xl mx-auto">
            In 5 minutes,<br />
            see your <span className="italic text-[#F59E0B]">next move</span>.
          </h2>
          <Link
            to="/quiz"
            className="group inline-flex items-center gap-3 bg-[#FAFAF7] text-[#0A0A0A] px-10 py-5 rounded-full font-medium text-base hover:bg-[#F59E0B] transition-all duration-300 hover:gap-5"
          >
            Start the free assessment
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mt-6">
            No signup required to start
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Home
